import * as React from 'react';
import type { GlobalProps } from '@bloomberg/media-ui-core';
import {
  classes,
  Slider,
  useCombinedRefs,
  useSlug,
} from '@bloomberg/media-ui-core';
import {
  ButtonDropdown,
  type DropdownOption,
} from '@bloomberg/media-ui-dropdown';

import { SecurityPill, Variant } from '../SecurityPill';
import type { TickerSecurity } from '../types';

import { useResizeForTickerTrack } from './useResizeForTickerTrack';

import styles from './styles.module.css';

export type TickerBarWithPillProps = GlobalProps & {
  displaySecurityName?: boolean;
  tickers: TickerSecurity[];
  trackClick?: (eventAction: string, eventLabel: string) => void;
  securities: DropdownOption[];
  onSecuritiesUpdate: (security?: DropdownOption | null) => void;
};

export type { DropdownOption };

export const TickerBarWithPill = React.forwardRef<
  HTMLDivElement,
  TickerBarWithPillProps
>(function TickerBarWithPillComponent(props, ref) {
  const {
    tickers = [],
    displaySecurityName = false,
    trackClick,
    securities,
    onSecuritiesUpdate,
    name = 'ticker-bar-with-pill',
  } = props;

  const [slugName] = useSlug(name);
  const [snapPositions, setSnapPositions] = React.useState<number[]>([]);
  const [isAtStart, setIsAtStart] = React.useState(true);
  const [isAtEnd, setIsAtEnd] = React.useState(true);
  const [securitiesCollapsed, setSecuritiesCollapsed] = React.useState(false);
  const refContainer = React.useRef<HTMLDivElement>(null);
  const combinedRef = useCombinedRefs(refContainer, ref);
  const trackRef = React.useRef<HTMLDivElement | null>(null);

  const getMenuPortal = React.useCallback((): Element | null => {
    if (typeof document === 'undefined') {
      return null;
    }

    return document.querySelector("[data-component='menu-portal']");
  }, []);

  const onSliderScroll = React.useCallback(
    (_scrollLeft: number, atStart: boolean, atEnd: boolean) => {
      setIsAtStart(atStart);
      setIsAtEnd(atEnd);
    },
    []
  );

  useResizeForTickerTrack({
    trackRef,
    tickers,
    onSliderScroll,
    setSecuritiesCollapsed,
    setSnapPositions,
  });

  const _onSecuritiesUpdate = React.useCallback(
    (security?: DropdownOption | null) => {
      if (trackClick && security) {
        trackClick('click', `tickerbar.dropdown_${security.value}`);
      }

      onSecuritiesUpdate(security);

      if (trackRef.current) {
        trackRef.current.scrollLeft = 0;
      }
    },
    [onSecuritiesUpdate]
  );

  const mappedTickers = React.useMemo(
    () =>
      tickers.map(
        ({
          url,
          ticker,
          name: displayName,
          percentChange1Day,
          price,
        }: TickerSecurity) => (
          <a
            href={url || `/quote/${ticker}`}
            className={styles.ticker}
            key={ticker}
            onClick={
              trackClick ? () => trackClick('ticker-click', ticker) : () => {}
            }
            role="link"
          >
            <SecurityPill
              ticker={ticker.replace(/:.*/, '')}
              displayName={displaySecurityName ? displayName : undefined}
              percentChange={percentChange1Day || 0}
              price={price}
              variant={Variant.refreshbar2024}
            />
          </a>
        )
      ),
    [tickers, trackClick]
  );

  return (
    <div
      ref={combinedRef}
      data-component={slugName}
      className={classes(
        [
          styles.bar,
          isAtStart && styles.isStart,
          isAtEnd && styles.isEnd,
          securitiesCollapsed && styles.securitiesCollapsed,
        ],
        props
      )}
    >
      <ButtonDropdown
        className={styles.securities}
        options={securities}
        onSelect={_onSecuritiesUpdate}
        unstyled
      />
      <div className={styles.menuPortal} data-component="menu-portal" />
      <Slider
        trackRef={trackRef}
        trackClick={trackClick}
        shadow={false}
        onSliderScroll={onSliderScroll}
        snapPositions={snapPositions}
      >
        <ButtonDropdown
          className={classes([styles.mobileSecurities, styles.scrollSnap])}
          options={securities}
          onSelect={_onSecuritiesUpdate}
          menuPortal={getMenuPortal()}
          unstyled
        />
        {mappedTickers}
      </Slider>
    </div>
  );
});
